import React, { useEffect, useState } from 'react';
import ResponsivePagination from 'react-responsive-pagination';
import 'react-responsive-pagination/themes/classic.css';
import Loader from '../../components/loader/loader';
import CommonModal from '../../components/modal/modal';
import {
  createData,
  deleteData,
  getListData,
  updateData,
  useDebounce,
  errorResponseMessage,
} from '../../hooks/api/utils';
import api from '../../hooks/services/service';
import { toast } from 'react-toastify';

const LIST_ENDPOINT = '/admin-panel/vegetation-types-list/';
const DETAIL_ENDPOINT = '/admin-panel/vegetation-types-detail/';
const ALPI_MONITOR_ENDPOINT = '/admin-panel/alpi-monitor/';

const emptyForm = {
  vegetation_type: '',
  yield_avg_tha: '',
  min_clamp: '',
  avg_clamp: '',
  max_clamp: '',
  ndvi_weight: '',
  ndmi_weight: '',
  ndvi_min: '',
  ndvi_avg: '',
  ndvi_max: '',
  ndmi_min: '',
  ndmi_avg: '',
  ndmi_max: '',
};

const NUMERIC_FIELDS = [
  'yield_avg_tha',
  'min_clamp',
  'avg_clamp',
  'max_clamp',
  'ndvi_weight',
  'ndmi_weight',
  'ndvi_min',
  'ndvi_avg',
  'ndvi_max',
  'ndmi_min',
  'ndmi_avg',
  'ndmi_max',
];

const formatNum = (value, decimals = 2) => {
  if (value === null || value === undefined || value === '') return '—';
  return Number(value).toFixed(decimals);
};

const toPayload = (form) => {
  const payload = { vegetation_type: form.vegetation_type.trim() };
  NUMERIC_FIELDS.forEach((key) => {
    payload[key] = parseFloat(form[key]);
  });
  return payload;
};

const VegetationType = () => {
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const pageSize = 10;
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 500, setPage);

  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [selectedName, setSelectedName] = useState('');
  const [deleteId, setDeleteId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [monitorLoading, setMonitorLoading] = useState(false);

  const items = data?.results ?? data?.data ?? [];
  const pageCount = Math.ceil((data?.count || items.length) / pageSize);

  const fetchData = async () => {
    setLoading(true);
    await getListData(
      LIST_ENDPOINT,
      setLoading,
      setData,
      page,
      pageSize,
      `&search=${debouncedSearch}`
    );
  };

  useEffect(() => {
    fetchData();
  }, [debouncedSearch, page]);

  const handlePageClick = (p) => setPage(p);

  const openAddModal = () => {
    setEditingId(null);
    setForm(emptyForm);
    setErrors({});
    setIsFormModalOpen(true);
  };

  const openEditModal = (row) => {
    setEditingId(row.id);
    setForm({
      vegetation_type: row.vegetation_type ?? '',
      yield_avg_tha: row.yield_avg_tha ?? '',
      min_clamp: row.min_clamp ?? '',
      avg_clamp: row.avg_clamp ?? '',
      max_clamp: row.max_clamp ?? '',
      ndvi_weight: row.ndvi_weight ?? '',
      ndmi_weight: row.ndmi_weight ?? '',
      ndvi_min: row.ndvi_min ?? '',
      ndvi_avg: row.ndvi_avg ?? '',
      ndvi_max: row.ndvi_max ?? '',
      ndmi_min: row.ndmi_min ?? '',
      ndmi_avg: row.ndmi_avg ?? '',
      ndmi_max: row.ndmi_max ?? '',
    });
    setErrors({});
    setIsFormModalOpen(true);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const validate = () => {
    const next = {};
    if (!form.vegetation_type.trim()) {
      next.vegetation_type = 'Vegetation type is required';
    }
    NUMERIC_FIELDS.forEach((key) => {
      const val = form[key];
      if (val === '' || val === null || val === undefined) {
        next[key] = 'Required';
      } else if (Number.isNaN(parseFloat(val))) {
        next[key] = 'Must be a number';
      }
    });
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;

    const payload = toPayload(form);
    const onDone = () => {
      setIsFormModalOpen(false);
      setEditingId(null);
      setForm(emptyForm);
      fetchData();
    };

    if (editingId) {
      await updateData(
        DETAIL_ENDPOINT,
        editingId,
        setLoading,
        payload,
        onDone,
        ''
      );
    } else {
      await createData(LIST_ENDPOINT, setLoading, payload, onDone, '');
    }
  };

  const confirmDelete = async () => {
    if (!deleteId) return;
    await deleteData(DETAIL_ENDPOINT, deleteId);
    setDeleteId(null);
    setSelectedName('');
    setIsDeleteModalOpen(false);
    setPage(1);
    fetchData();
  };

  const handleTriggerMonitor = async () => {
    setMonitorLoading(true);
    api.get(ALPI_MONITOR_ENDPOINT)
    .then(response => {
      toast.success(response?.data?.message);
    })
    .catch(error => {
      errorResponseMessage(error);
    })
    .finally(() => {
      setMonitorLoading(false);
    });
  };

  const renderField = (name, label, step = '0.01') => (
    <div className="col-md-4 col-sm-6 mb-3" key={name}>
      <label className="form-label">{label}</label>
      <input
        type="number"
        step={step}
        name={name}
        value={form[name]}
        onChange={handleChange}
        className={`form-control ${errors[name] ? 'is-invalid' : ''}`}
      />
      {errors[name] && (
        <div className="invalid-feedback d-block">{errors[name]}</div>
      )}
    </div>
  );

  return (
    <>
      <div className="rightContent">
        <div className="viewMain">
          <div className="row">
            <div className="col-md-12 bg-white left-col p-2">
              <div className="d-flex justify-content-between align-items-center mt-4 p-4 gap-4">
                <h4>Vegetation Types</h4>
                <div className="d-flex justify-content-end gap-3">
                  <div className="sortSearchArea search-margin mt-0">
                    <div className="search ms-auto">
                      <input
                        type="search"
                        placeholder="Search"
                        className="search-input"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                      />
                      <span
                        style={{
                          background: 'none',
                          border: 'none',
                          padding: 0,
                        }}
                      >
                        <img
                          src="../images/search-dark.svg"
                          className="img-fluid"
                          alt="Search"
                        />
                      </span>
                    </div>
                  </div>
                  <button
                    type="button"
                    className="transparent_btn"
                    onClick={handleTriggerMonitor}
                    disabled={monitorLoading}
                  >
                    {monitorLoading ? 'Starting…' : 'Run ALPI Monitor'}
                  </button>
                  <button
                    type="button"
                    className="blue_btn"
                    onClick={openAddModal}
                  >
                    Add Vegetation Type
                  </button>
                </div>
              </div>
              <div className="row">
                <div className="col-md-12">
                  <div className="pateintData">
                    <div className="treatmentData tableTransactionData table-responsive">
                      <table className="table equal-spacing-table">
                        <thead className="tableTransactionHead">
                          <tr>
                            <th scope="col">Vegetation Type</th>
                            <th scope="col">Yield average (t/ha)</th>
                            <th scope="col">Min clamp</th>
                            <th scope="col">Average clamp</th>
                            <th scope="col">Max clamp</th>
                            <th scope="col">Vegetation weight</th>
                            <th scope="col">Moisture weight</th>
                            <th scope="col">NDVI min</th>
                            <th scope="col">NDVI average</th>
                            <th scope="col">NDVI max</th>
                            <th scope="col">NDMI min</th>
                            <th scope="col">NDMI average</th>
                            <th scope="col">NDMI max</th>
                            <th scope="col">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="tableData-color">
                          {loading ? (
                            <tr>
                              <td colSpan="14">
                                <Loader />
                              </td>
                            </tr>
                          ) : items.length > 0 ? (
                            items.map((row) => (
                              <tr key={row.id}>
                                <td>{row.vegetation_type}</td>
                                <td>{formatNum(row.yield_avg_tha, 1)}</td>
                                <td>{formatNum(row.min_clamp)}</td>
                                <td>{formatNum(row.avg_clamp)}</td>
                                <td>{formatNum(row.max_clamp)}</td>
                                <td>{formatNum(row.ndvi_weight)}</td>
                                <td>{formatNum(row.ndmi_weight)}</td>
                                <td>{formatNum(row.ndvi_min)}</td>
                                <td>{formatNum(row.ndvi_avg)}</td>
                                <td>{formatNum(row.ndvi_max)}</td>
                                <td>{formatNum(row.ndmi_min)}</td>
                                <td>{formatNum(row.ndmi_avg)}</td>
                                <td>{formatNum(row.ndmi_max)}</td>
                                <td>
                                  <div className="d-flex flex-wrap gap-2 align-items-center justify-content-center justify-content-md-end action-buttons">
                                    <button
                                      type="button"
                                      className="border-0 bg-transparent"
                                      onClick={() => openEditModal(row)}
                                      title="Edit"
                                    >
                                      <img
                                        src="images/pencilIcon.svg"
                                        width="25"
                                        className="img-fluid"
                                        alt="Edit"
                                      />
                                    </button>
                                    <button
                                      type="button"
                                      className="border-0 bg-transparent"
                                      onClick={() => {
                                        setDeleteId(row.id);
                                        setSelectedName(row.vegetation_type);
                                        setIsDeleteModalOpen(true);
                                      }}
                                      title="Delete"
                                    >
                                      <img
                                        src="images/deleteGreyIcon.svg"
                                        width="20"
                                        className="img-fluid"
                                        alt="Delete"
                                      />
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan="14" className="text-center">
                                No vegetation types found.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>

                <div className="col-md-12">
                  <ResponsivePagination
                    className="pagination justify-content-center mt-5"
                    current={page}
                    total={pageCount || 0}
                    onPageChange={handlePageClick}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <CommonModal
        isOpen={isFormModalOpen}
        onRequestClose={() => {
          setIsFormModalOpen(false);
          setEditingId(null);
          setForm(emptyForm);
          setErrors({});
        }}
        title={editingId ? 'Edit Vegetation Type' : 'Add Vegetation Type'}
        style={{ content: { maxWidth: '900px', width: '95%' } }}
      >
        <div className="row">
          <div className="col-md-6 mb-3">
            <label className="form-label">Vegetation Type</label>
            <input
              type="text"
              name="vegetation_type"
              value={form.vegetation_type}
              onChange={handleChange}
              className={`form-control ${
                errors.vegetation_type ? 'is-invalid' : ''
              }`}
              placeholder="e.g. Wheat"
            />
            {errors.vegetation_type && (
              <div className="invalid-feedback d-block">
                {errors.vegetation_type}
              </div>
            )}
          </div>
          {renderField('yield_avg_tha', 'Yield average (t/ha)', '0.1')}
          {renderField('min_clamp', 'Min clamp')}
          {renderField('avg_clamp', 'Average clamp')}
          {renderField('max_clamp', 'Max clamp')}
          {renderField('ndvi_weight', 'Vegetation weight')}
          {renderField('ndmi_weight', 'Moisture weight')}
          {renderField('ndvi_min', 'NDVI min')}
          {renderField('ndvi_avg', 'NDVI average')}
          {renderField('ndvi_max', 'NDVI max')}
          {renderField('ndmi_min', 'NDMI min')}
          {renderField('ndmi_avg', 'NDMI average')}
          {renderField('ndmi_max', 'NDMI max')}
        </div>
        <div className="d-flex justify-content-end gap-3 mt-2">
          <button
            type="button"
            className="transparent_btn"
            onClick={() => {
              setIsFormModalOpen(false);
              setEditingId(null);
              setForm(emptyForm);
              setErrors({});
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            className="blue_btn"
            onClick={handleSave}
            disabled={loading}
          >
            Save
          </button>
        </div>
      </CommonModal>

      <CommonModal
        isOpen={isDeleteModalOpen}
        onRequestClose={() => {
          setIsDeleteModalOpen(false);
          setDeleteId(null);
          setSelectedName('');
        }}
        title="Confirm Delete"
      >
        <p className="text-center mb-4">
          Are you sure you want to delete <b>{selectedName}</b>?
        </p>
        <div className="d-flex justify-content-end gap-3">
          <button
            type="button"
            className="transparent_btn"
            onClick={() => {
              setIsDeleteModalOpen(false);
              setDeleteId(null);
              setSelectedName('');
            }}
          >
            Cancel
          </button>
          <button type="button" className="red_btn" onClick={confirmDelete}>
            Confirm
          </button>
        </div>
      </CommonModal>
    </>
  );
};

export default VegetationType;
